import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { accountsAPI, unwrapBills } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import {
  CheckCircle2, Loader2, ArrowDownNarrowWide, AlertTriangle,
  IndianRupee, FileText, Calendar, Upload, Image, X, Paperclip
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const resultingStatus = (outstanding, allocAmount) => {
  const remaining = outstanding - allocAmount;
  if (remaining <= 0) return { label: 'PAID', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' };
  if (allocAmount > 0) return { label: 'PARTIAL', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' };
  return { label: 'UNPAID', color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20' };
};

/**
 * Unified Payment Allocation Dialog.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open) => void
 *  - payment: { payment_id, amount, unallocated_amount, vendor_id }
 *  - vendorId: string (required)
 *  - onComplete: () => void — called after successful allocation
 *  - preSelectedBillId: string (optional) — pre-select a specific bill
 */
const PaymentAllocationDialog = ({
  open,
  onOpenChange,
  payment,
  vendorId,
  onComplete,
  preSelectedBillId,
}) => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allocations, setAllocations] = useState({}); // { bill_id: amount }
  const [allocating, setAllocating] = useState(false);
  // Proof attachment state
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [utrReference, setUtrReference] = useState('');
  const [proofNotes, setProofNotes] = useState('');
  const fileInputRef = useRef(null);

  const fetchBills = useCallback(async () => {
    if (!vendorId || !open) return;
    setLoading(true);
    try {
      const [unpaidRes, partialRes] = await Promise.all([
        accountsAPI.getVendorBills(vendorId, { status: 'Unpaid' }),
        accountsAPI.getVendorBills(vendorId, { status: 'Partial' }),
      ]);
      const allBills = [...unwrapBills(unpaidRes.data), ...unwrapBills(partialRes.data)];
      // Sort oldest-first (chronological) for FIFO
      allBills.sort((a, b) => (a.bill_date || '').localeCompare(b.bill_date || ''));
      setBills(allBills);
    } catch {
      toast.error('Failed to load open bills');
    }
    setLoading(false);
  }, [vendorId, open]);

  useEffect(() => {
    if (open) {
      fetchBills();
      setAllocations({});
      setProofFile(null);
      setProofPreview(null);
      setUtrReference('');
      setProofNotes('');
    }
  }, [open, fetchBills]);

  // Pre-select a bill if specified
  useEffect(() => {
    if (preSelectedBillId && bills.length > 0) {
      const bill = bills.find((b) => b.bill_id === preSelectedBillId);
      if (bill && payment) {
        const outstanding = bill.outstanding_amount ?? (bill.total_amount - (bill.paid_amount || 0));
        const amount = Math.min(payment.unallocated_amount || payment.amount, outstanding);
        setAllocations({ [preSelectedBillId]: amount });
      }
    }
  }, [preSelectedBillId, bills, payment]);

  const paymentAmount = payment?.unallocated_amount ?? payment?.amount ?? 0;

  const totalAllocated = useMemo(
    () => Object.values(allocations).reduce((sum, v) => sum + (parseFloat(v) || 0), 0),
    [allocations]
  );

  const unallocatedRemainder = paymentAmount - totalAllocated;

  const handleAllocationChange = (billId, value, maxOutstanding) => {
    const num = parseFloat(value) || 0;
    const clamped = Math.min(num, maxOutstanding);
    setAllocations((prev) => {
      const next = { ...prev };
      if (clamped <= 0) {
        delete next[billId];
      } else {
        next[billId] = clamped;
      }
      return next;
    });
  };

  const toggleBill = (billId, outstanding) => {
    setAllocations((prev) => {
      const next = { ...prev };
      if (next[billId] !== undefined) {
        delete next[billId];
      } else {
        // Calculate max we can allocate to this bill
        const currentTotal = Object.entries(next).reduce((s, [, v]) => s + (parseFloat(v) || 0), 0);
        const remaining = paymentAmount - currentTotal;
        next[billId] = Math.min(remaining, outstanding);
      }
      return next;
    });
  };

  const autoAllocateFIFO = () => {
    let remaining = paymentAmount;
    const newAlloc = {};
    for (const bill of bills) {
      if (remaining <= 0) break;
      const outstanding = bill.outstanding_amount ?? (bill.total_amount - (bill.paid_amount || 0));
      if (outstanding <= 0) continue;
      const amount = Math.min(remaining, outstanding);
      newAlloc[bill.bill_id] = amount;
      remaining -= amount;
    }
    setAllocations(newAlloc);
  };

  const handleProofFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Supported: JPEG, PNG, WEBP, PDF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }
    setProofFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setProofPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setProofPreview(null);
    }
  };

  const uploadProof = async (paymentId) => {
    if (!proofFile) return;
    const reader = new FileReader();
    return new Promise((resolve) => {
      reader.onload = async (ev) => {
        try {
          const base64 = ev.target.result.split('base64,')[1];
          const ext = proofFile.name.split('.').pop() || 'png';
          await accountsAPI.uploadPaymentProof(paymentId, {
            proof_base64: base64,
            proof_type: proofFile.type.includes('pdf') ? 'BANK_STATEMENT' : 'SCREENSHOT',
            file_ext: ext,
            notes: proofNotes || undefined,
          });
          resolve(true);
        } catch {
          toast.error('Proof upload failed — you can retry later');
          resolve(false);
        }
      };
      reader.readAsDataURL(proofFile);
    });
  };

  const handleSubmit = async () => {
    const allocationList = Object.entries(allocations)
      .filter(([, amount]) => parseFloat(amount) > 0)
      .map(([bill_id, amount]) => ({ bill_id, amount: parseFloat(amount) }));

    if (allocationList.length === 0) {
      toast.error('No bills selected for allocation');
      return;
    }

    setAllocating(true);
    try {
      // Phase 3 source hardening (May 2026): allocatePayment endpoint is
      // 410 Gone. Payment allocation is now inherent — the mark-paid
      // callback per-bill IS the allocation. This dialog should not be
      // reachable in the new flow; if it is, surface an actionable error
      // instead of crashing with "allocatePayment is not a function".
      throw new Error(
        'Payment allocation has moved. Record payments per-bill via "Mark Paid" on each bill row. Legacy unallocated payments cannot be created or re-allocated.',
      );
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || 'Allocation flow unavailable');
    }
    setAllocating(false);
  };

  const totalOutstanding = useMemo(
    () => bills.reduce((sum, b) => sum + (b.outstanding_amount ?? (b.total_amount - (b.paid_amount || 0))), 0),
    [bills]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] flex flex-col" data-testid="allocation-dialog">
        <DialogHeader>
          <DialogTitle className="font-manrope flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-emerald-400" />
            Allocate Payment to Bills
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Payment Summary Bar */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <p className="text-[10px] uppercase tracking-wider text-emerald-400/70 mb-1">Payment Amount</p>
              <p className="font-mono font-semibold text-emerald-400 text-lg" data-testid="payment-amount-display">
                {formatCurrency(paymentAmount)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/70 mb-1">Total Allocated</p>
              <p className={`font-mono font-semibold text-lg ${totalAllocated > paymentAmount ? 'text-rose-400' : 'text-blue-400'}`} data-testid="total-allocated-display">
                {formatCurrency(totalAllocated)}
              </p>
            </div>
            <div className={`p-3 rounded-lg border ${unallocatedRemainder < 0 ? 'bg-rose-500/10 border-rose-500/20' : 'bg-zinc-500/10 border-zinc-700/30'}`}>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400/70 mb-1">Unallocated</p>
              <p className={`font-mono font-semibold text-lg ${unallocatedRemainder < 0 ? 'text-rose-400' : 'text-zinc-300'}`} data-testid="unallocated-display">
                {formatCurrency(Math.max(unallocatedRemainder, 0))}
              </p>
            </div>
          </div>

          {/* Over-allocation guard */}
          {totalAllocated > paymentAmount && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30">
              <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
              <span className="text-sm text-rose-300">
                Over-allocation: {formatCurrency(totalAllocated)} exceeds payment of {formatCurrency(paymentAmount)}
              </span>
            </div>
          )}

          {/* Actions Row */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {bills.length} open bill(s) — {formatCurrency(totalOutstanding)} outstanding
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={autoAllocateFIFO}
              className="gap-1.5"
              data-testid="auto-allocate-btn"
            >
              <ArrowDownNarrowWide className="w-3.5 h-3.5" />
              Auto-Allocate (Oldest First)
            </Button>
          </div>

          {/* Bills Table */}
          <div className="flex-1 overflow-auto border border-border/50 rounded-lg min-h-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : bills.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No open bills found for this vendor</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Bill #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                    <TableHead className="w-[130px]">Allocate</TableHead>
                    <TableHead className="w-[90px] text-center">Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bills.map((bill) => {
                    const outstanding = bill.outstanding_amount ?? (bill.total_amount - (bill.paid_amount || 0));
                    const allocAmount = parseFloat(allocations[bill.bill_id]) || 0;
                    const status = resultingStatus(outstanding, allocAmount);
                    const isSelected = allocations[bill.bill_id] !== undefined;

                    return (
                      <TableRow
                        key={bill.bill_id}
                        className={`border-border/50 transition-colors ${isSelected ? 'bg-emerald-500/5' : ''}`}
                        data-testid={`allocation-row-${bill.bill_id}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleBill(bill.bill_id, outstanding)}
                            data-testid={`select-bill-${bill.bill_id}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{bill.bill_number}</TableCell>
                        <TableCell className="text-sm">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            {formatDate(bill.bill_date)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(bill.total_amount)}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-amber-400">{formatCurrency(outstanding)}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            max={outstanding}
                            step="0.01"
                            value={allocAmount || ''}
                            onChange={(e) => handleAllocationChange(bill.bill_id, e.target.value, outstanding)}
                            className="w-28 h-8 text-sm bg-background font-mono"
                            placeholder="0"
                            data-testid={`alloc-input-${bill.bill_id}`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={`text-[10px] ${status.color}`}>
                            {status.label}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
          {/* Proof Attachment Section */}
          <div className="border border-border/50 rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Payment Proof (Optional)</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* UTR / Reference */}
              <div>
                <label className="text-[11px] text-muted-foreground mb-1 block">UTR / Reference No.</label>
                <Input
                  value={utrReference}
                  onChange={(e) => setUtrReference(e.target.value)}
                  placeholder="UTR / Transaction ID"
                  className="h-8 text-sm bg-background font-mono"
                  data-testid="utr-reference-input"
                />
              </div>

              {/* Proof Upload */}
              <div>
                <label className="text-[11px] text-muted-foreground mb-1 block">Proof Attachment</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                  onChange={handleProofFileChange}
                  data-testid="proof-file-input"
                />
                {proofFile ? (
                  <div className="flex items-center gap-2 h-8 px-2 rounded border border-emerald-500/30 bg-emerald-500/5">
                    <Image className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    <span className="text-xs text-emerald-400 truncate flex-1">{proofFile.name}</span>
                    <button onClick={() => { setProofFile(null); setProofPreview(null); }} className="text-zinc-500 hover:text-zinc-300">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 w-full gap-1.5 text-xs"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-proof-btn"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    Upload Proof
                  </Button>
                )}
              </div>
            </div>

            {/* Proof Preview */}
            {proofPreview && (
              <div className="relative w-full max-h-32 overflow-hidden rounded border border-border/50" data-testid="proof-preview">
                <img src={proofPreview} alt="Proof preview" className="w-full h-full object-contain" />
              </div>
            )}

            {/* Notes */}
            <Textarea
              value={proofNotes}
              onChange={(e) => setProofNotes(e.target.value)}
              placeholder="Payment notes (optional)"
              className="min-h-[40px] h-10 text-sm bg-background resize-none"
              data-testid="proof-notes-input"
            />
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="skip-allocation-btn">
            Skip
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={allocating || totalAllocated <= 0 || totalAllocated > paymentAmount}
            data-testid="confirm-allocate-btn"
          >
            {allocating ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Allocate {formatCurrency(totalAllocated)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentAllocationDialog;
